import decode from 'jwt-decode'
import router from '@/router'
import store from '@/store/store'
import to from '@/utils/to'
import oh from 'object-hash'
import Repository from '@/repositories/RepositoryFactory'
const UserRepository = Repository.get('users')

const ID_TOKEN_KEY = 'id_token'
const ACCESS_TOKEN_KEY = 'access_token'

export { logout, requireAuth, getIdToken, getAccessToken, setAccessToken,
  setIdToken, isLoggedIn, endImpersonation }


/**
 * Logs out a user
 *
 */
function logout () {
  clearIdToken()
  clearAccessToken()
  router.push({ name: 'Login', query: { redirect: router.history.current.fullPath } })
}

/**
 * Ends an impersonation session
 */
async function endImpersonation () {
  const originalUser = store.getters.impersonationOriginalUser
  UserRepository.getUserSessionData(originalUser.id).
    then((sessionData) => {
      if (!sessionData) {
        clearIdToken()
        clearAccessToken()
        router.push({ name: 'Login', query: { redirect: router.history.current.fullPath } })
      } else if (sessionData.accounts && sessionData.accounts.length === 0) {
        clearIdToken()
        clearAccessToken()
        router.push({ name: 'Login', query: { redirect: router.history.current.fullPath } })
      } else {
        // Set user session data
        localStorage.setItem('SESSION_USER_ID', originalUser.id)
        store.dispatch('setMfaRequirement', false)
        store.dispatch('updateSessionUser', originalUser)
        store.dispatch('updateSessionUserPermissions', originalUser.userPermission)
        store.dispatch('updateUph', oh({ id: originalUser.id, permissions: originalUser.userPermission }))
        store.dispatch('updateUserSessionData', sessionData)
        store.dispatch('setImpersonationState', { isImpersonating: false, originalUser: {} })
        if (window.location.href.indexOf('profile') === -1) {
          router.push({ name: 'Profile' })
        }
      }
    })
    .catch((error) => {
      throw new Error(error)
    })
}

/**
 * Require auth middleware
 *
 * @param to - Where to
 * @param from - Where from
 * @param next - Next middleware
 */
function requireAuth (to, from, next) {
  if (!isLoggedIn() ||
    !store.getters.sessionUser.id ||
    (store.getters.mfaRequired && !store.getters.mfaValidated)) {
    next({
      path: '/login',
      query: { redirect: to.fullPath }
    })
  } else {
    next()
  }
}

/**
 * Gets ID token
 *
 * @return {String} User's ID token
 */
function getIdToken () {
  return localStorage.getItem(ID_TOKEN_KEY)
}

/**
 * Gets access token
 *
 * @return {String} User's access token
 */
function getAccessToken () {
  return localStorage.getItem(ACCESS_TOKEN_KEY)
}

/**
 * Clears ID token
 *
 */
function clearIdToken () {
  localStorage.removeItem(ID_TOKEN_KEY)
}

/**
 * Clears access token
 *
 */
function clearAccessToken () {
  localStorage.removeItem(ACCESS_TOKEN_KEY)
}

/**
 * Sets access token
 *
 */
function setAccessToken () {
  let accessToken = getParameterByName('access_token')
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
}

/**
 * Sets ID token
 *
 */
function setIdToken () {
  let idToken = getParameterByName('id_token')
  localStorage.setItem(ID_TOKEN_KEY, idToken)
}

/**
 * Checks is user is logged in
 *
 * @return {Boolean} True if logged in, false otherwise
 */
function isLoggedIn () {
  const idToken = getIdToken()
  return !!idToken && !isTokenExpired(idToken)
}

/**
 * Checks if token is expired
 *
 * @param token - User's token
 * @return {Boolean} True if token is expired, false otherwise
 */
function isTokenExpired (token) {
  const expirationDate = getTokenExpirationDate(token)
  return expirationDate < new Date()
}

/**
 * Extracts a token's expiration date
 *
 * @param encodedToken - User's encoded token
 * @return {Mixed} Date if expiration date exists, null otherwise
 */
function getTokenExpirationDate (encodedToken) {
  const token = decode(encodedToken)
  if (!token.exp) {
    return null
  }

  const date = new Date(0)
  date.setUTCSeconds(token.exp)

  return date
}

/**
 * Helper function that will allow us to extract the access_token and id_token
 *
 * @param Name - Either access_token or id_token
 * @return {String} Value of named parameter
 */
function getParameterByName (name) {
  let match = RegExp('[#&]' + name + '=([^&]*)').exec(window.location.hash)
  return match && decodeURIComponent(match[1].replace(/\+/g, ' '))
}
